import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { CalendarDays, MapPin, Clock, CalendarClock } from "lucide-react";
import { toast } from "sonner";

const ST = {
  confirmed: { l: "Confirmada", c: "bg-[#34D399]/15 text-[#0F766E]" },
  reschedule_requested: { l: "Reagendamento solicitado", c: "bg-[#F59E0B]/15 text-[#b26a00]" },
  completed: { l: "Concluída", c: "bg-[#94A3B8]/20 text-[#64748b]" },
};

export default function PatientAgenda() {
  const [sessions, setSessions] = useState([]);
  const load = () => api.get("/sessions").then((r) => setSessions(r.data));
  useEffect(() => { load(); }, []);

  const reschedule = async (id) => {
    await api.post(`/sessions/${id}/reschedule`);
    toast.success("Solicitação enviada ao seu profissional.");
    load();
  };

  const now = new Date().toISOString();
  const upcoming = sessions.filter((s) => s.scheduled_at >= now);
  const past = sessions.filter((s) => s.scheduled_at < now);

  const Row = ({ s }) => {
    const st = ST[s.status] || ST.confirmed;
    return (
      <Card data-testid={`session-${s.id}`} className="rounded-2xl p-5 border-[#E0F2FE] card-hover flex items-start justify-between gap-4">
        <div className="flex gap-4">
          <div className="w-12 h-12 rounded-xl bg-[#0F766E]/10 grid place-items-center shrink-0"><CalendarClock className="text-[#0F766E]" /></div>
          <div>
            <div className="font-semibold text-[#1F3B4D] capitalize">
              {new Date(s.scheduled_at).toLocaleString("pt-BR", { weekday: "long", day: "2-digit", month: "long" })}
            </div>
            <div className="text-sm text-[#94A3B8] flex items-center gap-3 mt-1 flex-wrap">
              <span className="flex items-center gap-1"><Clock size={13} /> {new Date(s.scheduled_at).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" })} · {s.duration}min</span>
              <span className="flex items-center gap-1"><MapPin size={13} /> {s.location || "A definir"}</span>
            </div>
            <Badge className={`${st.c} border-0 mt-2`}>{st.l}</Badge>
          </div>
        </div>
        {s.status === "confirmed" && s.scheduled_at >= now && (
          <Button variant="outline" size="sm" className="rounded-full border-[#E0F2FE] shrink-0"
            data-testid={`reschedule-${s.id}`} onClick={() => reschedule(s.id)}>Reagendar</Button>
        )}
      </Card>
    );
  };

  return (
    <div className="max-w-3xl mx-auto space-y-5 animate-fade-up">
      <div>
        <h1 className="font-heading text-2xl font-bold text-[#1F3B4D] flex items-center gap-2">
          <CalendarDays className="text-[#0F766E]" /> Agenda
        </h1>
        <p className="text-sm text-[#94A3B8]">Suas próximas sessões e histórico.</p>
      </div>
      <div className="space-y-3">
        <h3 className="font-heading font-bold text-[#1F3B4D]">Próximas</h3>
        {upcoming.length ? upcoming.map((s) => <Row key={s.id} s={s} />) : <p className="text-sm text-[#94A3B8]">Nenhuma sessão agendada.</p>}
      </div>
      {past.length > 0 && (
        <div className="space-y-3">
          <h3 className="font-heading font-bold text-[#1F3B4D]">Anteriores</h3>
          {past.map((s) => <Row key={s.id} s={s} />)}
        </div>
      )}
    </div>
  );
}
